import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";
import SaveIcon from "@mui/icons-material/Save";
import LogoutIcon from "@mui/icons-material/Logout";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import api from "../../api/api";
import { Backdrop, CircularProgress, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LOgo from "../../assets/Ntact.png";
import moment from "moment";
import ToastMessage from "../../utils/muitoast";
import { SUBCONTRACTOR_ROUTES } from "./routes";

// Public base for files uploaded through /v1/forms/upload-files. The admin-
// managed "Required Insurance Limits" document is served from here.
const FORMS_FILES_BASE = "https://hub2.ntactops.com/ntact/files/forms_files";

// User Information is a pre-step — it's not part of the numbered stepper below
const steps = [
  "Company Info",
  "Contacts",
  "HSE Info",
  "HSE Stats",
  "Safety",
  "Attestation",
];
const TOTAL_STEPS = steps.length; // 6

const currentYear = new Date().getFullYear();
const years = [currentYear - 1, currentYear - 2, currentYear - 3];

// Small "ⓘ" icon with a hover/tap tooltip — used on HSE Stats labels to
// explain which value the user should enter.
const InfoHint = ({ text }) => (
  <Tooltip
    title={text}
    arrow
    placement="top"
    enterTouchDelay={0}
    componentsProps={{
      tooltip: {
        sx: {
          fontSize: "12px",
          maxWidth: 280,
          lineHeight: 1.4,
          bgcolor: "#1f2937",
        },
      },
      arrow: { sx: { color: "#1f2937" } },
    }}
  >
    <InfoOutlinedIcon
      fontSize="small"
      className="text-gray-400 hover:text-gray-600 cursor-help ml-1 align-text-bottom"
      style={{ fontSize: 16 }}
    />
  </Tooltip>
);

// Each enclosed-document checkbox gets its own upload tag so the dashboard
// can later tell which document a saved file belongs to. Order here drives
// the checkbox render order on the Attestation page.
const ENCLOSED_DOCUMENTS = [
  { label: "Employee Training Curriculum", tag: "enc_training_curriculum" },
  { label: "HSE Manual Table of Contents", tag: "enc_hse_manual_toc" },
  { label: "Employee Training Cards", tag: "enc_training_cards" },
  { label: "Certificate Of Insurance Details", tag: "enc_insurance_cert" },
  { label: "W9 Documents", tag: "enc_w9" },
  { label: "Proof of Service", tag: "enc_proof_of_service" },
];

// Reusable input field — defined OUTSIDE the component to prevent focus loss
const InputField = ({ label, value, onChange, placeholder, required, maxLength, digitsOnly }) => (
  <div className="flex flex-col flex-1">
    <label className="text-sm font-medium mb-1 text-left">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type="text"
      value={value}
      maxLength={maxLength}
      onChange={(e) => {
        let val = e.target.value;
        if (digitsOnly) val = val.replace(/\D/g, "");
        if (maxLength && val.length > maxLength) return;
        onChange(val);
      }}
      placeholder={placeholder || label}
      className="border border-[#F3F3F5] bg-[#F3F3F5] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400"
    />
  </div>
);

// Radio button for Yes/No — defined OUTSIDE the component to prevent focus loss
const YesNoRadio = ({ value, onChange, noLabel }) => (
  <div className="flex gap-6 mt-1">
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input type="radio" checked={value === "Yes"} onChange={() => onChange("Yes")} className="accent-black" />
      Yes
    </label>
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input type="radio" checked={value === "No"} onChange={() => onChange("No")} className="accent-black" />
      {noLabel || "No"}
    </label>
  </div>
);

// File attachment component
const FileAttachment = ({ file, onChange, onRemove }) => (
  <div className="mt-2">
    {!file ? (
      <label className="inline-flex items-center gap-2 text-sm text-gray-600 border border-dashed border-gray-300 rounded-md px-4 py-2 cursor-pointer hover:bg-gray-50 transition">
        <AttachFileIcon fontSize="small" className="text-gray-500" />
        <span>Attach file</span>
        <input
          type="file"
          className="hidden"
          onChange={(e) => {
            if (e.target.files[0]) onChange(e.target.files[0]);
          }}
        />
      </label>
    ) : (
      <div className="inline-flex items-center gap-2 bg-gray-100 rounded-md px-3 py-2 text-sm">
        <AttachFileIcon fontSize="small" className="text-gray-500" />
        <span className="text-gray-700">{file.name || file}</span>
        <button onClick={onRemove} className="text-red-500 hover:text-red-700">
          <CloseIcon fontSize="small" />
        </button>
      </div>
    )}
  </div>
);

// Render the display name for either a fresh File or a saved server path.
// Saved paths arrive like ".../safetyProgram__foo.pdf" — strip that tag so
// the user sees the original filename.
const displayFileName = (file) => {
  if (!file) return "";
  if (file instanceof File) return file.name;
  const base = String(file).split("/").pop();
  const sep = base.indexOf("__");
  return sep > 0 ? base.substring(sep + 2) : base;
};

// Public URL helpers so we can preview a previously uploaded image inline.
const DOCUMENT_PUBLIC_BASE = "https://hub2.ntactops.com/ntact/files";
const DOCUMENT_PROXY_BASE = "https://ntactapi.brainymed.com/ntact/v1/forms/proxy?url=";
const toPublicDocUrl = (p) => {
  if (!p) return "";
  const marker = "/public/assets/";
  const idx = p.indexOf(marker);
  if (idx >= 0) return `${DOCUMENT_PUBLIC_BASE}/${p.substring(idx + marker.length)}`;
  return p;
};
const toProxyImgUrl = (p) => {
  const u = toPublicDocUrl(p);
  return u ? `${DOCUMENT_PROXY_BASE}${encodeURIComponent(u)}` : "";
};
const isImageFile = (n) => /\.(png|jpe?g|gif|heic|heif|webp|bmp|svg)$/i.test(n || "");

// Upload box matching the document upload design
const UploadBox = ({ file, onChange, onRemove, label }) => {
  const name = displayFileName(file);
  // Thumbnail: File → object URL; string path → proxy URL.
  let previewUrl = "";
  if (file instanceof File) {
    if (file.type?.startsWith("image/")) previewUrl = URL.createObjectURL(file);
  } else if (typeof file === "string" && isImageFile(file)) {
    previewUrl = toProxyImgUrl(file);
  }
  return (
    <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
      <div className="px-4 py-2 text-sm text-gray-600 font-medium text-center border-b border-gray-200">
        {label || "Upload supporting document"}
      </div>
      {!file ? (
        <label className="flex items-center justify-center gap-2 px-4 py-3 cursor-pointer hover:bg-gray-100 transition">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5m0 0l5 5m-5-5v12" />
          </svg>
          <span className="text-sm text-gray-600">Choose files to upload</span>
          <input
            type="file"
            className="hidden"
            accept="image/*,.pdf,.doc,.docx"
            onChange={(e) => {
              if (e.target.files[0]) onChange(e.target.files[0]);
            }}
          />
        </label>
      ) : (
        <div className="flex items-center justify-between px-4 py-3 gap-3">
          <div className="flex items-center gap-3 text-sm text-gray-700 min-w-0">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt={name}
                className="h-12 w-12 object-cover rounded border border-gray-200"
              />
            ) : (
              <AttachFileIcon fontSize="small" className="text-gray-500" />
            )}
            <div className="flex flex-col min-w-0">
              <span className="truncate">{name}</span>
              {typeof file === "string" && (
                <a
                  href={toPublicDocUrl(file)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-blue-600 hover:underline truncate"
                >
                  View previously uploaded file
                </a>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <label className="text-xs text-gray-600 border border-gray-300 rounded-md px-2 py-1 cursor-pointer hover:bg-gray-100">
              Replace
              <input
                type="file"
                className="hidden"
                accept="image/*,.pdf,.doc,.docx"
                onChange={(e) => {
                  if (e.target.files[0]) onChange(e.target.files[0]);
                }}
              />
            </label>
            <button onClick={onRemove} className="text-red-500 hover:text-red-700">
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SubcontractorForm = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("allow") || sessionStorage.getItem("subcontractorToken") || sessionStorage.getItem("accessToken");
  const mode = searchParams.get("mode") || "new"; // "new" | "continue"
  // Where to send the user after a successful save/submit. Admin edits from
  // the /subcontractors dashboard pass returnTo=dashboard; public flows fall
  // back to the subcontractor login landing page.
  const returnTo = searchParams.get("returnTo") === "dashboard"
    ? "/subcontractors"
    : SUBCONTRACTOR_ROUTES.login;
  // In "new" mode, any previously-stored subcontractor id belongs to a
  // different record (left behind by an earlier continue/retrieve flow).
  // Adopting it would make save overwrite that record, so we drop it.
  const [subId, setSubId] = useState(() => {
    if (mode === "new") {
      sessionStorage.removeItem("subcontractorSubId");
      return null;
    }
    return sessionStorage.getItem("subcontractorSubId") || null;
  });
  const [loader, setLoader] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [dataLoaded, setDataLoaded] = useState(false);
  // User Information is a pre-step shown before the numbered stepper.
  // On "new" → show it first. On "continue" → skip it (phone already verified, draft loaded).
  const [showUserInfo, setShowUserInfo] = useState(mode !== "continue");

  // Phone validation OTP states
  const [phoneVerified, setPhoneVerified] = useState(mode === "continue");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [otpResendTimer, setOtpResendTimer] = useState(0);
  const [otpSending, setOtpSending] = useState(false);
  const otpInputsRef = React.useRef([]);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("error");
  const [enclosedErrors, setEnclosedErrors] = useState(() => new Set());

  const showToast = (msg, severity = "error") => {
    setToastMessage(msg);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const [formData, setFormData] = useState({
    // Page 1: User Information
    firstName: "",
    lastName: "",
    phone: "",         // personal phone for OTP validation
    emailAddress: "",

    // Page 2: Company Info
    subcontractorName: "",
    telephoneNumber: "",
    streetAddress: "",
    faxNumber: "",
    city: "",
    websiteAddress: "",
    provinceState: "",
    postalCode: "",
    president: "",
    vicePresident: "",
    treasurer: "",
    servicesProvided: "",
    servicesSelected: [],
    servicesOther: "",

    // Page 3: Contacts
    contacts: {
      insurance: { title: "", telephone: "", fax: "", email: "" },
      requestingBids: { title: "", telephone: "", fax: "", email: "" },
      contractorEvaluation: { title: "", telephone: "", fax: "", email: "" },
    },

    // Page 4: HSE Info
    hseNameTitle: "",
    hsePhone: "",
    hseEmail: "",

    // Page 5: HSE Stats
    hseStats: {
      totalManhours: { [years[0]]: "", [years[1]]: "", [years[2]]: "" },
      trir: { [years[0]]: "", [years[1]]: "", [years[2]]: "" },
      lwc: { [years[0]]: "", [years[1]]: "", [years[2]]: "" },
      lwdr: { [years[0]]: "", [years[1]]: "", [years[2]]: "" },
      emr: "",
      fatalities_last_3_years: "",
      serious_incidents_last_3_years: "",
    },

    // Page 6: Safety
    safetyProgram: "",
    safetyProgramAttachment: null,
    safetyManual: "",
    safetyManualAttachment: null,
    drugAlcoholTesting: "",
    onlineDatabases: {
      ISNetworld: "",
      NCMS: "",
      Avetta: "",
      Veriforce: "",
      TPS: "",
    },
    // Subscription IDs captured when the user answers "Yes" — TPS is excluded
    // per requirement (only the other four carry IDs).
    onlineDatabaseIds: {
      ISNetworld: "",
      NCMS: "",
      Avetta: "",
      Veriforce: "",
    },
    oshaCitation: "",
    oshaCitationAttachment: null,
    thirdPartyMedical: "",
    thirdPartyMedicalAttachment: null,
    jsaProcess: "",
    rootCauseAnalysis: "",
    hseOrientation: "",
    hseOrientationAttachment: null,

    // Page 7: Attestation
    printName: "",
    position: "",
    signature: "",
    attestationDate: "",
    enclosedDocuments: [],
    // Per-document file uploads keyed by the document label.
    enclosedDocumentFiles: {},
    // Expiry date captured for the Certificate Of Insurance Details doc.
    insuranceExpiryDate: "",
    // Insurance coverage amounts captured against the configured minimums.
    coverages: {},
  });

  // Insurance coverage minimums (loaded from /v1/subcontractor/insurance_scaling)
  const [insuranceConfig, setInsuranceConfig] = useState({ minimums: {}, labels: {} });
  const [showInsuranceLimits, setShowInsuranceLimits] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get("/v1/subcontractor/insurance_scaling");
        const data = resp?.data?.data || resp?.data;
        if (!cancelled && data) setInsuranceConfig(data);
      } catch (err) {
        // Non-fatal — the form just won't show inline minimum hints.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const coverageKeys = Object.keys(insuranceConfig.minimums || {});
  // Admin-uploaded "Required Insurance Limits" document (managed from the
  // Subcontractor dashboard's Insurance modal). When present, the "Check
  // Limits" button renders and opens this file; when absent, the button is
  // hidden since there's nothing to show.
  const insuranceLimitDocUrl = insuranceConfig.limit_doc?.file_name
    ? `${FORMS_FILES_BASE}/${insuranceConfig.limit_doc.file_name}`
    : "";

  // Redirect to login if no token, otherwise load saved data if subId exists
  useEffect(() => {
    if (!token) {
      // navigate("/subcontractor-login");
      return;
    }
    if (mode === "continue") {
      // TODO: Backend dev will create an endpoint that returns the draft record (status "D")
      // for the verified phone number. For now we just attempt loadSavedData() if subId
      // was set, and place the user on page 1 of the stepper.
      loadDraftForContinue();
    } else if (subId) {
      loadSavedData();
    }
  }, []);

  const loadDraftForContinue = async () => {
    const savedId = sessionStorage.getItem("subcontractorSubId");
    if (savedId) {
      setSubId(savedId);
      await loadSavedData(savedId);
    }
    setShowUserInfo(false);
    setCurrentPage(1);
  };

  const loadSavedData = async (id) => {
    const useId = id || subId;
    if (!useId) return;
    setLoader(true);
    try {
      const response = await api.get(`/v1/subcontractor/get?id=${useId}`);
      if (response.status === 200 && response.data) {
        const saved = response.data;
        setFormData((prev) => {
          const updated = { ...prev };

          // Load User Info from firstname/lastname/phone/email columns
          updated.firstName = saved.firstname || prev.firstName || "";
          updated.lastName = saved.lastname || prev.lastName || "";
          updated.phone = saved.phone || prev.phone || "";
          updated.emailAddress = saved.email || prev.emailAddress || "";

          // Load Company Info
          if (saved.companyinfo) {
            const ci = saved.companyinfo;
            updated.subcontractorName = ci.subcontractorName || "";
            updated.telephoneNumber = ci.telephoneNumber || "";
            updated.streetAddress = ci.streetAddress || "";
            updated.faxNumber = ci.faxNumber || "";
            updated.city = ci.city || "";
            updated.websiteAddress = ci.websiteAddress || "";
            updated.provinceState = ci.provinceState || "";
            updated.postalCode = ci.postalCode || "";
            updated.president = ci.president || "";
            updated.vicePresident = ci.vicePresident || "";
            updated.treasurer = ci.treasurer || "";
            updated.servicesProvided = ci.servicesProvided || "";
            updated.servicesSelected = Array.isArray(ci.servicesSelected)
              ? ci.servicesSelected
              : [];
            updated.servicesOther = ci.servicesOther || "";
          }

          // Load Contacts
          if (saved.contacts) {
            updated.contacts = {
              insurance: saved.contacts.insurance || prev.contacts.insurance,
              requestingBids: saved.contacts.requestingBids || prev.contacts.requestingBids,
              contractorEvaluation: saved.contacts.contractorEvaluation || prev.contacts.contractorEvaluation,
            };
          }

          // Load HSE Info
          if (saved.hseinfo) {
            updated.hseNameTitle = saved.hseinfo.hseNameTitle || "";
            updated.hsePhone = saved.hseinfo.hsePhone || "";
            updated.hseEmail = saved.hseinfo.hseEmail || "";
          }

          // Load HSE Stats
          if (saved.hsestats) {
            updated.hseStats = saved.hsestats;
          }

          // Load Safety
          if (saved.safety) {
            const s = saved.safety;
            updated.safetyProgram = s.safetyProgram || "";
            updated.safetyManual = s.safetyManual || "";
            updated.drugAlcoholTesting = s.drugAlcoholTesting || "";
            updated.onlineDatabases = s.onlineDatabases || prev.onlineDatabases;
            updated.onlineDatabaseIds = s.onlineDatabaseIds || prev.onlineDatabaseIds;
            updated.oshaCitation = s.oshaCitation || "";
            updated.thirdPartyMedical = s.thirdPartyMedical || "";
            updated.jsaProcess = s.jsaProcess || "";
            updated.rootCauseAnalysis = s.rootCauseAnalysis || "";
            updated.hseOrientation = s.hseOrientation || "";
          }

          // Load Attestation
          if (saved.attestation) {
            updated.printName = saved.attestation.printName || "";
            updated.position = saved.attestation.position || "";
            updated.signature = saved.attestation.signature || "";
            updated.attestationDate = saved.attestation.attestationDate || "";
            updated.enclosedDocuments = saved.attestation.enclosedDocuments || [];
            updated.insuranceExpiryDate = saved.attestation.insuranceExpiryDate || "";
            updated.coverages = saved.attestation.coverages || {};
          }

          // Pre-fill attachments from previously saved `documents`. We map the
          // filename tag (e.g. "safetyProgram__foo.pdf") back to the state
          // field that controls its UploadBox so the existing file shows up
          // in edit mode. The value stored is just the server path string —
          // UploadBox already renders that as the filename, and buildFormData
          // skips non-File values so we never re-upload a path.
          const docs = saved.documents || [];
          const tagToStateField = {
            safetyProgram: "safetyProgramAttachment",
            safetyManual: "safetyManualAttachment",
            oshaCitation: "oshaCitationAttachment",
            thirdPartyMedical: "thirdPartyMedicalAttachment",
            hseOrientation: "hseOrientationAttachment",
          };
          const tagToEnclosedLabel = {
            enc_training_curriculum: "Employee Training Curriculum",
            enc_hse_manual_toc: "HSE Manual Table of Contents",
            enc_training_cards: "Employee Training Cards",
            enc_insurance_cert: "Certificate Of Insurance Details",
            enc_w9: "W9 Documents",
            enc_proof_of_service: "Proof of Service",
          };
          const nextEnclosedFiles = { ...(updated.enclosedDocumentFiles || {}) };
          docs.forEach((path) => {
            const name = (path || "").split("/").pop();
            const sep = name.indexOf("__");
            if (sep <= 0) return;
            const tag = name.substring(0, sep);
            if (tagToStateField[tag]) {
              updated[tagToStateField[tag]] = path;
            } else if (tagToEnclosedLabel[tag]) {
              nextEnclosedFiles[tagToEnclosedLabel[tag]] = path;
            }
          });
          updated.enclosedDocumentFiles = nextEnclosedFiles;

          return updated;
        });
        setDataLoaded(true);
      }
    } catch (err) {
      console.error("Error loading saved data:", err);
    } finally {
      setLoader(false);
    }
  };

  const buildPayload = (status = "draft") => {
    const payload = {
      firstname: formData.firstName,
      lastname: formData.lastName,
      phone: formData.phone,
      email: formData.emailAddress,
      companyinfo: {
        subcontractorName: formData.subcontractorName,
        telephoneNumber: formData.telephoneNumber,
        streetAddress: formData.streetAddress,
        faxNumber: formData.faxNumber,
        city: formData.city,
        websiteAddress: formData.websiteAddress,
        provinceState: formData.provinceState,
        postalCode: formData.postalCode,
        president: formData.president,
        vicePresident: formData.vicePresident,
        treasurer: formData.treasurer,
        servicesProvided: formData.servicesProvided,
        servicesSelected: formData.servicesSelected || [],
        servicesOther: formData.servicesOther || "",
      },
      contacts: formData.contacts,
      hseinfo: {
        hseNameTitle: formData.hseNameTitle,
        hsePhone: formData.hsePhone,
        hseEmail: formData.hseEmail,
      },
      hsestats: formData.hseStats,
      safety: {
        safetyProgram: formData.safetyProgram,
        safetyManual: formData.safetyManual,
        drugAlcoholTesting: formData.drugAlcoholTesting,
        onlineDatabases: formData.onlineDatabases,
        onlineDatabaseIds: formData.onlineDatabaseIds,
        oshaCitation: formData.oshaCitation,
        thirdPartyMedical: formData.thirdPartyMedical,
        jsaProcess: formData.jsaProcess,
        rootCauseAnalysis: formData.rootCauseAnalysis,
        hseOrientation: formData.hseOrientation,
      },
      attestation: {
        printName: formData.printName,
        position: formData.position,
        signature: formData.signature,
        attestationDate: formData.attestationDate,
        enclosedDocuments: formData.enclosedDocuments,
        insuranceExpiryDate: formData.insuranceExpiryDate,
        coverages: formData.coverages || {},
      },
      status: status,
    };
    if (subId) {
      payload.id = parseInt(subId);
    }
    return payload;
  };

  // Maps each attachment state field to a short tag the backend filename will
  // start with, so we can tell later which question the document belongs to.
  // Separator `__` is unusual in real filenames and easy to parse.
  const ATTACHMENT_TAGS = {
    safetyProgramAttachment: "safetyProgram",
    safetyManualAttachment: "safetyManual",
    oshaCitationAttachment: "oshaCitation",
    thirdPartyMedicalAttachment: "thirdPartyMedical",
    hseOrientationAttachment: "hseOrientation",
  };

  // Backend /v1/subcontractor/save expects multipart: `request` JSON string + `files` list.
  // Only attach File instances — previously saved attachments come back as path strings.
  const buildFormData = (status) => {
    const payload = buildPayload(status);
    const fd = new FormData();
    fd.append("request", JSON.stringify(payload));

    Object.entries(ATTACHMENT_TAGS).forEach(([key, tag]) => {
      const f = formData[key];
      if (f instanceof File) {
        // Prefix filename with the tag so the dashboard can label it later.
        const taggedName = `${tag}__${f.name}`;
        fd.append("files", f, taggedName);
      }
    });

    // Attestation page: per-document attachments
    ENCLOSED_DOCUMENTS.forEach(({ label, tag }) => {
      const f = formData.enclosedDocumentFiles?.[label];
      if (f instanceof File) {
        fd.append("files", f, `${tag}__${f.name}`);
      }
    });
    return fd;
  };

  const handleSaveAndExit = async () => {
    setLoader(true);
    setSaveMessage("");
    try {
      const fd = buildFormData("D");
      const response = await api.post("/v1/subcontractor/save", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.status === 200) {
        if (response.data?.id) {
          setSubId(response.data.id);
          sessionStorage.setItem("subcontractorSubId", response.data.id);
        }
        setSaveMessage("Data saved successfully!");
        setTimeout(() => navigate(returnTo), 1500);
      } else {
        setSaveMessage("Failed to save. Please try again.");
      }
    } catch (err) {
      console.error("Save error:", err);
      setSaveMessage("Failed to save. Please try again.");
    } finally {
      setLoader(false);
    }
  };

  const handleSubmit = async () => {
    // Validate all pages before submit
    const allMissing = [];
    for (let p = 1; p <= TOTAL_STEPS; p++) {
      const m = validatePage(p);
      if (m.length > 0) allMissing.push(...m);
    }
    if (allMissing.length > 0) {
      const { enclosed, other } = splitEnclosedMissing(allMissing);
      setEnclosedErrors(enclosed);
      const parts = [];
      if (other.length) parts.push(`Please fill in: ${other.join(", ")}`);
      if (enclosed.size) parts.push("Please fill Enclosed Documents");
      showToast(parts.join(" · "));
      return;
    }
    setEnclosedErrors(new Set());

    setLoader(true);
    setSaveMessage("");
    try {
      const fd = buildFormData("A");
      const response = await api.post("/v1/subcontractor/save", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.status === 200) {
        if (response.data?.id) {
          setSubId(response.data.id);
          sessionStorage.setItem("subcontractorSubId", response.data.id);
        }
        setSaveMessage("Form submitted successfully!");
        setTimeout(() => navigate(returnTo), 1500);
      } else {
        setSaveMessage("Submission failed. Please try again.");
      }
    } catch (err) {
      console.error("Submit error:", err);
      setSaveMessage("Submission failed. Please try again.");
    } finally {
      setLoader(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleContactChange = (contactType, field, value) => {
    setFormData((prev) => ({
      ...prev,
      contacts: {
        ...prev.contacts,
        [contactType]: { ...prev.contacts[contactType], [field]: value },
      },
    }));
  };

  const handleStatChange = (stat, year, value) => {
    setFormData((prev) => ({
      ...prev,
      hseStats: {
        ...prev.hseStats,
        [stat]: { ...prev.hseStats[stat], [year]: value },
      },
    }));
  };

  const handleDatabaseChange = (db, value) => {
    setFormData((prev) => ({
      ...prev,
      onlineDatabases: { ...prev.onlineDatabases, [db]: value },
    }));
  };

  // OTP resend countdown
  React.useEffect(() => {
    if (otpResendTimer <= 0) return;
    const t = setInterval(() => setOtpResendTimer((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [otpResendTimer]);

  const handleSendPhoneOtp = async () => {
    if (!formData.phone || formData.phone.replace(/\D/g, "").length < 7) {
      setSaveMessage("Please enter a valid phone number first.");
      return;
    }
    setOtpSending(true);
    setOtpError("");
    try {
      const resp = await api.post("/v1/subcontractor/send_otp", {
        phone_number: formData.phone,
        is_new: "N",
      });
      if (resp.status === 200) {
        // With is_new="N" we're just verifying the caller's phone — the
        // returned subcontractor_id belongs to whatever existing record uses
        // that phone, which we must NOT adopt here or save would overwrite it.
        setShowOtpModal(true);
        setOtpResendTimer(59);
        setOtpDigits(["", "", "", ""]);
      }
    } catch (err) {
      setSaveMessage("Failed to send OTP. Please try again.");
    } finally {
      setOtpSending(false);
    }
  };

  const handleOtpDigitChange = (idx, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otpDigits];
    next[idx] = val.slice(-1);
    setOtpDigits(next);
    if (val && idx < 3) otpInputsRef.current[idx + 1]?.focus();
  };

  const handleOtpDigitKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otpDigits[idx] && idx > 0) {
      otpInputsRef.current[idx - 1]?.focus();
    }
  };

  const handleVerifyPhoneOtp = async () => {
    setOtpError("");
    const code = otpDigits.join("");
    if (code.length !== 4) {
      setOtpError("Please enter the full 4-digit code.");
      return;
    }
    setLoader(true);
    try {
      const resp = await api.post("/v1/subcontractor/validate_otp", {
        phone_number: formData.phone,
        otp: code,
        flag: "N",
      });
      if (resp.status === 200) {
        setPhoneVerified(true);
        setShowOtpModal(false);
        setSaveMessage("Phone verified successfully!");
        setTimeout(() => setSaveMessage(""), 3000);
      }
    } catch (err) {
      setOtpError("Invalid OTP. Please try again.");
    } finally {
      setLoader(false);
    }
  };

  // User Info pre-step: validate then move into the numbered stepper
  const handleUserInfoNext = () => {
    if (
      !formData.firstName.trim() ||
      !formData.lastName.trim() ||
      !formData.emailAddress.trim()
    ) {
      showToast("Please fill in all required fields before proceeding.");
      return;
    }
    if (!phoneVerified) {
      showToast("Please verify your phone number before proceeding.");
      return;
    }
    setShowUserInfo(false);
    setCurrentPage(1);
  };

  // --- Validation helpers ---------------------------------------------------
  const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());
  const isDecimal = (v) => /^\d+(\.\d+)?$/.test((v || "").toString().trim());
  const isInteger = (v) => /^\d+$/.test((v || "").toString().trim());
  // Keep uploaded file references: the backend returns previously saved paths
  // as strings, so either a fresh File instance or a non-empty path counts.
  const hasAttachment = (v) => v instanceof File || (typeof v === "string" && v.length > 0);

  const validatePage = (page) => {
    const missing = [];
    const invalid = [];

    if (page === 1) {
      // Company Info — core required fields
      if (!formData.subcontractorName.trim()) missing.push("Subcontractor Name");
      if (!formData.telephoneNumber.trim()) missing.push("Telephone Number");
      if (!formData.streetAddress.trim()) missing.push("Street Address");
      if (!formData.city.trim()) missing.push("City");
      if (!formData.provinceState.trim()) missing.push("Province / State");
      if (!formData.postalCode.trim()) missing.push("Postal Code / Zip");
      if (formData.telephoneNumber && !isInteger(formData.telephoneNumber))
        invalid.push("Telephone Number must be digits only");
      if (formData.faxNumber && !isInteger(formData.faxNumber))
        invalid.push("Fax Number must be digits only");
    } else if (page === 2) {
      // Contacts — at least Insurance contact required; validate formats
      const c = formData.contacts || {};
      const ins = c.insurance || {};
      if (!ins.title?.trim()) missing.push("Insurance Contact — Title");
      if (!ins.telephone?.trim()) missing.push("Insurance Contact — Telephone");
      if (!ins.email?.trim()) missing.push("Insurance Contact — Email");
      ["insurance", "requestingBids", "contractorEvaluation"].forEach((key) => {
        const row = c[key] || {};
        const labelPrefix =
          key === "insurance"
            ? "Insurance"
            : key === "requestingBids"
            ? "Requesting Bids"
            : "Contractor Evaluation";
        if (row.telephone && !isInteger(row.telephone))
          invalid.push(`${labelPrefix} Telephone must be digits only`);
        if (row.fax && !isInteger(row.fax))
          invalid.push(`${labelPrefix} Fax must be digits only`);
        if (row.email && !isEmail(row.email))
          invalid.push(`${labelPrefix} Email is not valid`);
      });
    } else if (page === 3) {
      // HSE Info
      if (!formData.hseNameTitle.trim()) missing.push("HSE Name / Title");
      if (!formData.hsePhone.trim()) missing.push("HSE Phone");
      if (!formData.hseEmail.trim()) missing.push("HSE Email");
      if (formData.hsePhone && !isInteger(formData.hsePhone))
        invalid.push("HSE Phone must be digits only");
      if (formData.hseEmail && !isEmail(formData.hseEmail))
        invalid.push("HSE Email is not valid");
    } else if (page === 4) {
      // HSE Stats — require every year cell + the three scorecard inputs, all numeric
      const stats = formData.hseStats || {};
      const numericRows = [
        { key: "totalManhours", label: "Total Manhours", integer: true },
        { key: "trir", label: "TRIR", integer: false },
        { key: "lwc", label: "LWC", integer: true },
        { key: "lwdr", label: "LWDR", integer: false },
      ];
      numericRows.forEach(({ key, label, integer }) => {
        years.forEach((y) => {
          const v = stats[key]?.[y];
          if (v === undefined || v === null || String(v).trim() === "") {
            missing.push(`${label} (${y})`);
          } else if (integer ? !isInteger(v) : !isDecimal(v)) {
            invalid.push(`${label} (${y}) must be a number`);
          } else if (key === "totalManhours" && Number(v) === 0) {
            // Total Manhours of 0 is meaningless (would divide by zero in
            // TRIR / LWDR rate calculations) — block it explicitly.
            invalid.push(`${label} (${y}) must be greater than 0`);
          }
        });
      });
      if (!String(stats.emr ?? "").trim()) missing.push("EMR");
      else if (!isDecimal(stats.emr)) invalid.push("EMR must be a number");
      if (!String(stats.fatalities_last_3_years ?? "").trim())
        missing.push("Fatalities (Last 3 Years)");
      else if (!isInteger(stats.fatalities_last_3_years))
        invalid.push("Fatalities must be a whole number");
      if (!String(stats.serious_incidents_last_3_years ?? "").trim())
        missing.push("Serious Incidents (Last 3 Years)");
      else if (!isInteger(stats.serious_incidents_last_3_years))
        invalid.push("Serious Incidents must be a whole number");
    } else if (page === 5) {
      // Safety — every Yes/No must be answered, every revealed upload box required
      if (!formData.safetyProgram) missing.push("Q1 Safety Program");
      if (formData.safetyProgram === "No" && !hasAttachment(formData.safetyProgramAttachment))
        missing.push("Q1 Attachment (explain Safety Program)");
      if (!formData.safetyManual) missing.push("Q2 Safety Manual");
      if (formData.safetyManual === "Yes" && !hasAttachment(formData.safetyManualAttachment))
        missing.push("Q2 Attachment (Table of Contents)");
      if (!formData.drugAlcoholTesting) missing.push("Q3 Drug & Alcohol");
      Object.entries(formData.onlineDatabases || {}).forEach(([db, val]) => {
        if (!val) missing.push(`Q4 ${db}`);
        if (val === "Yes" && db !== "TPS") {
          const id = (formData.onlineDatabaseIds?.[db] || "").trim();
          if (!id) missing.push(`Q4 ${db} — ID`);
        }
      });
      if (!formData.oshaCitation) missing.push("Q5 OSHA Citation");
      if (formData.oshaCitation === "Yes" && !hasAttachment(formData.oshaCitationAttachment))
        missing.push("Q5 Attachment (OSHA Citation)");
      if (!formData.thirdPartyMedical) missing.push("Q6 Third-party Medical");
      // if (formData.thirdPartyMedical === "No")
      //   missing.push("Q6 Third-party Medical Consortium is required (cannot submit with 'No')");
      if (formData.thirdPartyMedical === "Yes" && !hasAttachment(formData.thirdPartyMedicalAttachment))
        missing.push("Q6 Attachment (Proof of Service)");
      if (!formData.jsaProcess) missing.push("Q7 JSA Process");
      if (!formData.rootCauseAnalysis) missing.push("Q8 Root Cause Analysis");
      if (!formData.hseOrientation) missing.push("Q9 HSE Orientation");
      if (formData.hseOrientation === "Yes" && !hasAttachment(formData.hseOrientationAttachment))
        missing.push("Q9 Attachment (Training Curriculum)");
    } else if (page === 6) {
      // Attestation — core required fields
      if (!formData.printName.trim()) missing.push("Print Name");
      if (!formData.signature.trim()) missing.push("Signature");
      if (!formData.attestationDate.trim()) missing.push("Date");
      // All 5 enclosed documents are mandatory — the row must be checked AND
      // have a file attached. The HSE Manual Table of Contents row accepts
      // Q2's safety-manual upload as a substitute, so it doesn't need a
      // separate file.
      ENCLOSED_DOCUMENTS.forEach(({ label }) => {
        const isChecked = (formData.enclosedDocuments || []).includes(label);
        if (!isChecked) {
          missing.push(`${label} — required`);
          return;
        }
        const f = formData.enclosedDocumentFiles?.[label];
        const linked =
          label === "HSE Manual Table of Contents"
            ? formData.safetyManualAttachment
            : null;
        if (!hasAttachment(f) && !hasAttachment(linked)) {
          missing.push(`${label} — attachment`);
        }
      });
      // Insurance Certificate requires an expiry date.
      if (
        (formData.enclosedDocuments || []).includes(
          "Certificate Of Insurance Details",
        ) &&
        !formData.insuranceExpiryDate?.trim()
      ) {
        missing.push("Certificate of Insurance — Expiry Date");
      }
    }

    return [...missing, ...invalid];
  };

  // Pulls the enclosed-document entries out of a validatePage() result so we can
  // highlight them inline (red border per row) and show a single, focused
  // toast instead of dumping every label into the toast text.
  const splitEnclosedMissing = (missing) => {
    const enclosed = new Set();
    const other = [];
    missing.forEach((m) => {
      if (m === "Certificate of Insurance — Expiry Date") {
        enclosed.add("Certificate Of Insurance Details");
        return;
      }
      const match = ENCLOSED_DOCUMENTS.find(
        (d) => m === `${d.label} — required` || m === `${d.label} — attachment`,
      );
      if (match) enclosed.add(match.label);
      else other.push(m);
    });
    return { enclosed, other };
  };

  const goToNextPage = () => {
    const missing = validatePage(currentPage);
    if (missing.length > 0) {
      const { enclosed, other } = splitEnclosedMissing(missing);
      setEnclosedErrors(enclosed);
      const parts = [];
      if (other.length) parts.push(`Please fill in: ${other.join(", ")}`);
      if (enclosed.size) parts.push("Please fill Enclosed Documents");
      showToast(parts.join(" · "));
      return;
    }
    setEnclosedErrors(new Set());
    if (currentPage < TOTAL_STEPS) setCurrentPage(currentPage + 1);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    } else if (mode === "new") {
      // From stepper page 1, go back to User Info pre-step
      setShowUserInfo(true);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50">
      <Backdrop open={loader} sx={{ zIndex: 9999 }}>
        <CircularProgress />
      </Backdrop>

      <div className="max-w-5xl mx-auto py-6 px-4">
        {/* Top Bar with Save & Exit */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <img src={LOgo} alt="NTACT" className="h-12" />
          </div>
          <h1 className="text-lg font-bold text-center flex-1">
            Subcontractor Safety Pre-Qualification Form
          </h1>
          {!showUserInfo && (
            <button
              onClick={handleSaveAndExit}
              className="flex items-center gap-2 bg-gray-900 text-white text-sm px-4 py-2 rounded-md hover:bg-gray-800 transition"
            >
              <SaveIcon fontSize="small" />
              Save & Exit
            </button>
          )}
          {showUserInfo && <div className="w-[120px]" />}
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div className={`text-center text-sm py-2 px-4 rounded-md mb-4 ${
            saveMessage.includes("success") ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
          }`}>
            {saveMessage}
          </div>
        )}

        {/* Document Info */}
        <div className="flex justify-between text-xs text-gray-600 mb-6 border-b pb-3">
          <div className="flex flex-col text-start">
            <div className="font-semibold text-[#000000] leading-5 text-xs font-font1">Document Name:</div>
            <div className="font-normal text-[#000000] leading-5 text-xs font-font1">Subcontractor Safety Pre-<br /> Qualification Form</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-[#000000] leading-5 text-xs font-font1">Section:</div>
            <div className="font-normal text-[#000000] leading-5 text-xs font-font1">HSE-18</div>
          </div>
          <div className="text-right">
            <div className="font-semibold text-[#000000] leading-5 text-xs font-font1">Revision: 1</div>
            <div className="font-normal text-[#000000] leading-5 text-xs font-font1">Date: {moment().format("YYYY-MM-DD")}</div>
            <div className="font-normal text-[#000000] leading-5 text-xs font-font1">Document Number: 18-7-A</div>
          </div>
        </div>

        {showUserInfo ? (
          <>
            {/* Pre-step: User Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-gray-900 text-white px-6 py-3 text-sm font-semibold tracking-wide text-left">
                USER INFORMATION
              </div>
              <div className="p-6">
                <h3 className="font-semibold text-sm mb-4 text-left">Personal Details</h3>
                <div className="mb-4 flex flex-col">
                  <label className="text-sm font-medium mb-1 text-left">First Name <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.firstName} onChange={(e) => handleChange("firstName", e.target.value)} placeholder="First Name" className="border border-[#F3F3F5] bg-[#F3F3F5] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400" />
                </div>
                <div className="mb-4 flex flex-col">
                  <label className="text-sm font-medium mb-1 text-left">Last Name <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.lastName} onChange={(e) => handleChange("lastName", e.target.value)} placeholder="Last Name" className="border border-[#F3F3F5] bg-[#F3F3F5] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400" />
                </div>
                <div className="mb-4 flex flex-col">
                  <label className="text-sm font-medium mb-1 text-left">Phone Number <span className="text-red-500">*</span></label>
                  <div className="flex items-center gap-2">
                    <input
                      type="tel"
                      value={formData.phone}
                      maxLength={10}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "");
                        if (val.length <= 10) {
                          handleChange("phone", val);
                          setPhoneVerified(false);
                        }
                      }}
                      placeholder="Phone Number"
                      className="flex-1 border border-[#F3F3F5] bg-[#F3F3F5] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400"
                    />
                    {phoneVerified ? (
                      <span className="text-green-600 text-sm font-medium px-3 py-2 bg-green-50 rounded-md whitespace-nowrap">
                        Verified
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleSendPhoneOtp}
                        disabled={otpSending}
                        className="bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-gray-800 transition disabled:opacity-60 whitespace-nowrap"
                      >
                        {otpSending ? "Sending..." : "Validate"}
                      </button>
                    )}
                  </div>
                </div>
                <div className="mb-4 flex flex-col">
                  <label className="text-sm font-medium mb-1 text-left">Email Address <span className="text-red-500">*</span></label>
                  <input type="email" value={formData.emailAddress} onChange={(e) => handleChange("emailAddress", e.target.value)} placeholder="Email Address" className="border border-[#F3F3F5] bg-[#F3F3F5] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400" />
                </div>
                <p className="text-xs text-gray-400 mt-4 text-left flex items-center gap-1">
                  <span className="inline-block w-4 h-4 rounded-full border border-gray-400 text-[10px] flex items-center justify-center">i</span>
                  All fields marked with <span className="text-red-500">*</span> are required
                </p>
              </div>
              <div className="flex justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
                <button
                  onClick={() => navigate(SUBCONTRACTOR_ROUTES.login)}
                  className="border border-gray-300 bg-white text-gray-700 px-6 py-2 rounded-md text-sm hover:bg-gray-100 flex items-center gap-1"
                >
                  &lt; Back
                </button>
                <button
                  onClick={handleUserInfoNext}
                  className="bg-gray-900 text-white px-6 py-2 rounded-md text-sm hover:bg-gray-800 flex items-center gap-1"
                >
                  Next &gt;
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
        {/* Stepper */}
        <div className="flex justify-center items-center gap-4 mb-8">
          {steps.map((step, index) => {
            const pageNum = index + 1;
            const isCompleted = pageNum < currentPage;
            const isCurrent = pageNum === currentPage;
            return (
              <div key={step} className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors ${
                    isCompleted
                      ? "bg-gray-800 text-white border-gray-800"
                      : isCurrent
                      ? "bg-gray-800 text-white border-gray-800"
                      : "bg-white text-gray-400 border-gray-300"
                  }`}
                >
                  {isCompleted ? <CheckIcon fontSize="small" /> : pageNum}
                </div>
                <span className={`text-[10px] mt-1 ${isCurrent ? "font-bold" : "text-gray-400"}`}>
                  {step}
                </span>
              </div>
            );
          })}
        </div>

        {/* Page Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Section Header */}
          <div className="bg-gray-900 text-white px-6 py-3 text-sm font-semibold tracking-wide text-left">
            {steps[currentPage - 1].toUpperCase()}
          </div>

          <div className="p-6">
            {/* PAGE 1: Company Info */}
            {currentPage === 1 && (
              <>
                <h3 className="font-semibold text-sm mb-4 text-left">Subcontractor Information</h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <InputField label="Subcontractor Name" value={formData.subcontractorName} onChange={(v) => handleChange("subcontractorName", v)} required />
                  <InputField label="Telephone Number" value={formData.telephoneNumber} onChange={(v) => handleChange("telephoneNumber", v)} required maxLength={15} digitsOnly />
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <InputField label="Street Address" value={formData.streetAddress} onChange={(v) => handleChange("streetAddress", v)} required />
                  <InputField label="Fax Number" value={formData.faxNumber} onChange={(v) => handleChange("faxNumber", v)} maxLength={15} digitsOnly />
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <InputField label="City" value={formData.city} onChange={(v) => handleChange("city", v)} required />
                  <InputField label="Website Address" value={formData.websiteAddress} onChange={(v) => handleChange("websiteAddress", v)} />
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <InputField label="Province / State" value={formData.provinceState} onChange={(v) => handleChange("provinceState", v)} required />
                  <InputField label="Postal Code / Zip" value={formData.postalCode} onChange={(v) => handleChange("postalCode", v)} required />
                </div>

                <h3 className="font-semibold text-sm mb-4 text-left">Officers</h3>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <InputField label="President" value={formData.president} onChange={(v) => handleChange("president", v)} />
                  <InputField label="Vice President" value={formData.vicePresident} onChange={(v) => handleChange("vicePresident", v)} />
                  <InputField label="Treasurer" value={formData.treasurer} onChange={(v) => handleChange("treasurer", v)} />
                </div>

                <h3 className="font-semibold text-sm mb-3 text-left border-t border-gray-200 pt-2">Services provided to NTACT</h3>
                {(() => {
                  const SERVICE_OPTIONS = [
                    "Crane Services",
                    "Transportation/ Hot-shot",
                    "Painting/ Blasting",
                    "Insulation",
                    "Scaffolding",
                    "X-Ray/ NDT",
                    "Testing Services",
                    "Fencing",
                    "Trash/ Porta-john services",
                    "Electrical Testing/ services",
                    "Dirt work",
                    "Commissioning",
                    "Other",
                  ];
                  const selected = formData.servicesSelected || [];
                  const toggle = (opt) => {
                    const next = selected.includes(opt)
                      ? selected.filter((s) => s !== opt)
                      : [...selected, opt];
                    handleChange("servicesSelected", next);
                    // Keep a friendly summary in servicesProvided for analytics/exports
                    const summaryParts = next.filter((s) => s !== "Other");
                    if (next.includes("Other") && (formData.servicesOther || "").trim()) {
                      summaryParts.push(`Other: ${formData.servicesOther.trim()}`);
                    }
                    handleChange("servicesProvided", summaryParts.join(", "));
                    // Clear the other text if Other is unticked
                    if (!next.includes("Other") && formData.servicesOther) {
                      handleChange("servicesOther", "");
                    }
                  };
                  return (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
                        {SERVICE_OPTIONS.map((opt) => {
                          const active = selected.includes(opt);
                          return (
                            <label
                              key={opt}
                              className={`flex items-center gap-2 text-sm cursor-pointer px-3 py-2 rounded-lg border transition-all ${
                                active
                                  ? "bg-[#8B1818]/5 border-[#8B1818]/40 text-gray-900"
                                  : "bg-gray-50 border-gray-200 hover:border-gray-300 text-gray-700"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={active}
                                onChange={() => toggle(opt)}
                                className="accent-[#8B1818] w-4 h-4"
                              />
                              <span className="text-left">{opt}</span>
                            </label>
                          );
                        })}
                      </div>
                      {selected.includes("Other") && (
                        <textarea
                          value={formData.servicesOther}
                          onChange={(e) => {
                            const v = e.target.value;
                            handleChange("servicesOther", v);
                            const others = (formData.servicesSelected || []).filter(
                              (s) => s !== "Other",
                            );
                            const summary = [...others];
                            if (v.trim()) summary.push(`Other: ${v.trim()}`);
                            handleChange("servicesProvided", summary.join(", "));
                          }}
                          placeholder="Describe the other services your organization will be providing..."
                          className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400 min-h-[80px]"
                        />
                      )}
                    </>
                  );
                })()}
              </>
            )}

            {/* PAGE 2: Contacts */}
            {currentPage === 2 && (
              <>
                <div className="mb-6">
                  <h4 className="font-semibold text-sm mb-3 text-left">4. Contact for Insurance Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <InputField label="Title" value={formData.contacts.insurance.title} onChange={(v) => handleContactChange("insurance", "title", v)} />
                    <InputField label="Telephone" value={formData.contacts.insurance.telephone} onChange={(v) => handleContactChange("insurance", "telephone", v)} maxLength={15} digitsOnly />
                    <InputField label="Fax" value={formData.contacts.insurance.fax} onChange={(v) => handleContactChange("insurance", "fax", v)} maxLength={15} digitsOnly />
                    <InputField label="Email" value={formData.contacts.insurance.email} onChange={(v) => handleContactChange("insurance", "email", v)} />
                  </div>
                </div>
                <div className="mb-6">
                  <h4 className="font-semibold text-sm mb-3 text-left">5. Contact for Requesting Bids</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <InputField label="Title" value={formData.contacts.requestingBids.title} onChange={(v) => handleContactChange("requestingBids", "title", v)} />
                    <InputField label="Telephone" value={formData.contacts.requestingBids.telephone} onChange={(v) => handleContactChange("requestingBids", "telephone", v)} maxLength={15} digitsOnly />
                    <InputField label="Fax" value={formData.contacts.requestingBids.fax} onChange={(v) => handleContactChange("requestingBids", "fax", v)} maxLength={15} digitsOnly />
                    <InputField label="Email" value={formData.contacts.requestingBids.email} onChange={(v) => handleContactChange("requestingBids", "email", v)} />
                  </div>
                </div>
                <div className="mb-6">
                  <h4 className="font-semibold text-sm mb-3 text-left">6. Contractor Evaluation Completed By</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <InputField label="Title" value={formData.contacts.contractorEvaluation.title} onChange={(v) => handleContactChange("contractorEvaluation", "title", v)} />
                    <InputField label="Telephone" value={formData.contacts.contractorEvaluation.telephone} onChange={(v) => handleContactChange("contractorEvaluation", "telephone", v)} maxLength={15} digitsOnly />
                    <InputField label="Fax" value={formData.contacts.contractorEvaluation.fax} onChange={(v) => handleContactChange("contractorEvaluation", "fax", v)} maxLength={15} digitsOnly />
                    <InputField label="Email" value={formData.contacts.contractorEvaluation.email} onChange={(v) => handleContactChange("contractorEvaluation", "email", v)} />
                  </div>
                </div>
              </>
            )}

            {/* PAGE 3: HSE Info */}
            {currentPage === 3 && (
              <>
                <h3 className="font-semibold text-sm mb-4 text-left">Highest Ranking HSE Professional in the Firm</h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <InputField label="Name / Title" value={formData.hseNameTitle} onChange={(v) => handleChange("hseNameTitle", v)} />
                  <InputField label="Phone" value={formData.hsePhone} onChange={(v) => handleChange("hsePhone", v)} maxLength={15} digitsOnly />
                </div>
                <InputField label="Email" value={formData.hseEmail} onChange={(v) => handleChange("hseEmail", v)} />
              </>
            )}

            {/* PAGE 4: HSE Stats */}
            {currentPage === 4 && (
              <>
                <p className="text-sm text-gray-500 mb-4 text-left">
                  Enter your company's health and safety incident data for the previous three years.
                </p>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 text-sm font-semibold w-1/4">Health & Safety Incidents</th>
                      {years.map((y) => (
                        <th key={y} className="text-center py-2 px-3 text-sm font-semibold">{y}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      {
                        key: "totalManhours",
                        label: "Total Manhours",
                        integer: true,
                        hint: "Total hours worked by all employees that year. e.g. 50 employees × 2,000 hrs ≈ 100,000.",
                      },
                      {
                        key: "trir",
                        label: "Total Recordable Incident Rate (TRIR)",
                        integer: false,
                        hint: "(Number of OSHA recordable injuries × 200,000) ÷ Total manhours. Enter as a decimal rate like 0.50.",
                      },
                      {
                        key: "lwc",
                        label: "Lost Workday Cases (LWC)",
                        integer: true,
                        hint: "Count of injury/illness cases that caused days away from work. Whole number (e.g. 0, 1, 2).",
                      },
                      {
                        key: "lwdr",
                        label: "Lost Workday Incident Rate (LWDR)",
                        integer: false,
                        hint: "(Lost workday cases × 200,000) ÷ Total manhours. Decimal rate, e.g. 0.25.",
                      },
                    ].map((row) => (
                      <tr key={row.key} className="border-b">
                        <td className="py-3 px-3 text-sm font-medium text-left">
                          {row.label}
                          <InfoHint text={row.hint} />
                        </td>
                        {years.map((y) => (
                          <td key={y} className="py-3 px-3">
                            <input
                              type="text"
                              inputMode={row.integer ? "numeric" : "decimal"}
                              value={formData.hseStats[row.key][y]}
                              onChange={(e) => {
                                // Strip everything but digits (+ a single dot
                                // for decimal rows). Prevents non-numeric input.
                                let v = e.target.value;
                                if (row.integer) {
                                  v = v.replace(/\D/g, "");
                                } else {
                                  v = v.replace(/[^0-9.]/g, "");
                                  // allow only one dot
                                  const firstDot = v.indexOf(".");
                                  if (firstDot !== -1) {
                                    v = v.substring(0, firstDot + 1) + v.substring(firstDot + 1).replace(/\./g, "");
                                  }
                                }
                                // Total Manhours can never be 0 (or 0…X) —
                                // it would divide by zero in TRIR / LWDR
                                // calculations, so strip leading zeros at
                                // input time. "0" → "", "0100" → "100".
                                if (row.key === "totalManhours") {
                                  v = v.replace(/^0+/, "");
                                }
                                handleStatChange(row.key, y, v);
                              }}
                              className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm text-center focus:outline-none focus:border-gray-400"
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Additional Safety Metrics — scorecard inputs */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <p className="text-sm font-semibold text-gray-700 mb-1 text-left">Additional Safety Metrics</p>
                  <p className="text-xs text-gray-500 mb-3 text-left">Used to auto-calculate your safety scorecard.</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium text-left block mb-1">
                        EMR (Experience Modifier Rate)
                        <InfoHint text="Your current workers' comp Experience Modifier Rate from the insurance carrier. 1.00 is industry average; lower is better (e.g. 0.85)." />
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formData.hseStats.emr || ""}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.]/g, "");
                          setFormData({
                            ...formData,
                            hseStats: { ...formData.hseStats, emr: val },
                          });
                        }}
                        placeholder="e.g. 0.85"
                        className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-left block mb-1">
                        Fatalities (Last 3 Years)
                        <InfoHint text="Total work-related fatalities across your company in the last 3 calendar years. Whole number (0 if none)." />
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={3}
                        value={formData.hseStats.fatalities_last_3_years || ""}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "");
                          setFormData({
                            ...formData,
                            hseStats: { ...formData.hseStats, fatalities_last_3_years: val },
                          });
                        }}
                        placeholder="0"
                        className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-left block mb-1">
                        Serious Incidents (Last 3 Years)
                        <InfoHint text="Count of serious or life-altering incidents (amputations, hospitalizations, etc.) in the last 3 years. Whole number." />
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={4}
                        value={formData.hseStats.serious_incidents_last_3_years || ""}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "");
                          setFormData({
                            ...formData,
                            hseStats: { ...formData.hseStats, serious_incidents_last_3_years: val },
                          });
                        }}
                        placeholder="0"
                        className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* PAGE 5: Safety */}
            {currentPage === 5 && (
              <div className="space-y-6">
                {/* Q1 */}
                <div className="border-b border-gray-200 p-3">
                  <div className="flex items-start gap-3">
                    <span className="bg-gray-800 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shrink-0">1</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-left">Do you have a company safety program?</p>
                      <YesNoRadio value={formData.safetyProgram} onChange={(v) => handleChange("safetyProgram", v)} noLabel="No (Please explain details in attachment)" />
                      {formData.safetyProgram === "No" && (
                        <UploadBox file={formData.safetyProgramAttachment} onChange={(f) => handleChange("safetyProgramAttachment", f)} onRemove={() => handleChange("safetyProgramAttachment", null)} />
                      )}
                    </div>
                  </div>
                </div>

                {/* Q2 */}
                <div className="border-b border-gray-200  p-3">
                  <div className="flex items-start gap-3">
                    <span className="bg-gray-800 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shrink-0">2</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-left">Do you have a safety manual?</p>
                      <YesNoRadio value={formData.safetyManual} onChange={(v) => handleChange("safetyManual", v)} noLabel="No (If yes, please attach a table of contents)" />
                      {formData.safetyManual === "Yes" && (
                        <UploadBox file={formData.safetyManualAttachment} onChange={(f) => handleChange("safetyManualAttachment", f)} onRemove={() => handleChange("safetyManualAttachment", null)} />
                      )}
                    </div>
                  </div>
                </div>

                {/* Q3 */}
                <div className="border-b border-gray-200 p-3">
                  <div className="flex items-start gap-3">
                    <span className="bg-gray-800 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shrink-0">3</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-left">Does your company have a drug and alcohol testing program?</p>
                      <YesNoRadio value={formData.drugAlcoholTesting} onChange={(v) => handleChange("drugAlcoholTesting", v)} />
                    </div>
                  </div>
                </div>

                {/* Q4 - Online Databases */}
                <div className="border-b border-gray-200 p-3">
                  <div className="flex items-start gap-3">
                    <span className="bg-gray-800 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shrink-0">4</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium mb-3 text-left">Are you currently subscribed to the following online safety databases?</p>
                      <div className="space-y-3 ml-2">
                        {Object.keys(formData.onlineDatabases).map((db) => {
                          const answer = formData.onlineDatabases[db];
                          const needsId = db !== "TPS" && answer === "Yes";
                          return (
                            <div key={db} className="flex flex-col gap-1">
                              <div className="flex items-center gap-4 flex-wrap">
                                <span className="text-sm w-24 text-left">{db}:</span>
                                <YesNoRadio
                                  value={answer}
                                  onChange={(v) => handleDatabaseChange(db, v)}
                                />
                                {needsId && (
                                  <div className="flex items-center gap-2">
                                    <label className="text-xs text-gray-600">
                                      ID <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      value={formData.onlineDatabaseIds?.[db] || ""}
                                      onChange={(e) =>
                                        setFormData((prev) => ({
                                          ...prev,
                                          onlineDatabaseIds: {
                                            ...(prev.onlineDatabaseIds || {}),
                                            [db]: e.target.value,
                                          },
                                        }))
                                      }
                                      placeholder={`Enter ${db} ID`}
                                      className="border border-gray-200 bg-gray-50 rounded-md px-2 py-1 text-sm focus:outline-none focus:border-gray-400 w-48"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Q5 */}
                <div className="border-b border-gray-200 p-3">
                  <div className="flex items-start gap-3">
                    <span className="bg-gray-800 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shrink-0">5</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-left">Has your company ever been issued an OSHA Citation?</p>
                      <YesNoRadio value={formData.oshaCitation} onChange={(v) => handleChange("oshaCitation", v)} noLabel="No (If yes, please explain in an attachment)" />
                      {formData.oshaCitation === "Yes" && (
                        <UploadBox file={formData.oshaCitationAttachment} onChange={(f) => handleChange("oshaCitationAttachment", f)} onRemove={() => handleChange("oshaCitationAttachment", null)} />
                      )}
                    </div>
                  </div>
                </div>

                {/* Q6 */}
                <div className="border-b border-gray-200 p-3">
                  <div className="flex items-start gap-3">
                    <span className="bg-gray-800 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shrink-0">6</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium  text-left">Do you currently have a third-party medical consortium for case management of injuries? (i.e., Axiom, Extreme MD, Occurred)</p>
                      <YesNoRadio value={formData.thirdPartyMedical} onChange={(v) => handleChange("thirdPartyMedical", v)} noLabel="No (a third-party medical consortium is required to submit this form)" />
                      {formData.thirdPartyMedical === "Yes" && (
                        <>
                          <p className="text-xs text-red-500 mt-2 text-left">Please upload proof of service.</p>
                          <UploadBox
                            file={formData.thirdPartyMedicalAttachment}
                            onChange={(f) => handleChange("thirdPartyMedicalAttachment", f)}
                            onRemove={() => handleChange("thirdPartyMedicalAttachment", null)}
                          />
                        </>
                      )}
                      {/* {formData.thirdPartyMedical === "No" && (
                        <p className="text-xs text-red-600 mt-2 text-left font-medium">
                          A third-party medical consortium is required. You cannot submit this form with this answer.
                        </p>
                      )} */}
                    </div>
                  </div>
                </div>

                {/* Q7 */}
                <div className="border-b border-gray-200 p-3">
                  <div className="flex items-start gap-3">
                    <span className="bg-gray-800 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shrink-0">7</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-left">Do you have a Job Safety Analysis (JSA) process in place?</p>
                      <YesNoRadio value={formData.jsaProcess} onChange={(v) => handleChange("jsaProcess", v)} />
                    </div>
                  </div>
                </div>

                {/* Q8 */}
                <div className="border-b border-gray-200 p-3">
                  <div className="flex items-start gap-3">
                    <span className="bg-gray-800 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shrink-0">8</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-left">Is there a Root Cause Analysis process used for investigations, near misses, and environmental spills?</p>
                      <YesNoRadio value={formData.rootCauseAnalysis} onChange={(v) => handleChange("rootCauseAnalysis", v)} />
                    </div>
                  </div>
                </div>

                {/* Q9 */}
                <div className="border-b border-gray-200 p-3">
                  <div className="flex items-start gap-3">
                    <span className="bg-gray-800 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shrink-0">9</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-left">Do you have an HSE Orientation Program for new hires and newly hired or promoted supervisors?</p>
                      <YesNoRadio value={formData.hseOrientation} onChange={(v) => handleChange("hseOrientation", v)} noLabel="No (If Yes, attach training curriculum)" />
                      {formData.hseOrientation === "Yes" && (
                        <UploadBox file={formData.hseOrientationAttachment} onChange={(f) => handleChange("hseOrientationAttachment", f)} onRemove={() => handleChange("hseOrientationAttachment", null)} />
                      )}
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* PAGE 6: Attestation */}
            {currentPage === 6 && (
              <>
                {/* Attestation Statement */}
                <div className="bg-[#FEF2F2] rounded-md p-4 mb-6">
                  <p className="text-sm text-gray-700 leading-relaxed text-left">
                    I attest that all information provided in previous pages is accurate according to the company's policies and procedures. The sub-contractor will comply with and adhere to all relevant Health, Safety, and Environmental standards set forth by NTACT and their clients.
                  </p>
                </div>

                {/* Print Name & Position */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <InputField label="Print Name" value={formData.printName} onChange={(v) => handleChange("printName", v)} required />
                  <InputField label="Position" value={formData.position} onChange={(v) => handleChange("position", v)} required />
                </div>

                {/* Signature & Date */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="flex flex-col flex-1">
                    <label className="text-sm font-medium mb-1 text-left">
                      Signature <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.signature}
                      onChange={(e) => handleChange("signature", e.target.value)}
                      placeholder="Type your full name to sign"
                      className="border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400"
                    />
                  </div>
                  <div className="flex flex-col flex-1">
                    <label className="text-sm font-medium mb-1 text-left">
                      Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.attestationDate}
                      onChange={(e) => handleChange("attestationDate", e.target.value)}
                      className="border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400"
                    />
                  </div>
                </div>

                {/* Enclosed Documents */}
                <div>
                  <h3 className="font-semibold text-sm mb-2 text-left">
                    Enclosed Documents <span className="text-red-500">*</span>
                  </h3>
                  <p className="text-sm text-gray-500 mb-4 text-left">All documents below are required. Check each item and upload the corresponding file before submitting.</p>
                  {enclosedErrors.size > 0 && (
                    <div className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 text-left">
                      Please fill the highlighted enclosed document{enclosedErrors.size === 1 ? "" : "s"} below.
                    </div>
                  )}
                  <div className="space-y-3">
                    {ENCLOSED_DOCUMENTS.map(({ label }) => {
                      const checked = formData.enclosedDocuments.includes(label);
                      const hasError = enclosedErrors.has(label);
                      const clearError = () =>
                        setEnclosedErrors((prev) => {
                          if (!prev.has(label)) return prev;
                          const next = new Set(prev);
                          next.delete(label);
                          return next;
                        });
                      // Q2's safety-manual upload doubles as the HSE Manual
                      // Table of Contents enclosed doc — surface it here so
                      // the user doesn't have to re-upload.
                      const linkedFromQ2 =
                        label === "HSE Manual Table of Contents"
                          ? formData.safetyManualAttachment
                          : null;
                      const file =
                        formData.enclosedDocumentFiles?.[label] || linkedFromQ2 || null;
                      const isLinked = !formData.enclosedDocumentFiles?.[label] && !!linkedFromQ2;
                      return (
                        <div
                          key={label}
                          className={`border rounded-md transition ${
                            hasError
                              ? "border-red-400 bg-red-50/40 ring-1 ring-red-200"
                              : "border-gray-200"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2 pr-3">
                            <label className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 flex-1 min-w-0">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  clearError();
                                  setFormData((prev) => {
                                    const nextList = prev.enclosedDocuments.includes(label)
                                      ? prev.enclosedDocuments.filter((d) => d !== label)
                                      : [...prev.enclosedDocuments, label];
                                    // Unchecking a document also clears its file.
                                    const nextFiles = { ...(prev.enclosedDocumentFiles || {}) };
                                    if (!nextList.includes(label)) delete nextFiles[label];
                                    return {
                                      ...prev,
                                      enclosedDocuments: nextList,
                                      enclosedDocumentFiles: nextFiles,
                                    };
                                  });
                                }}
                                className="accent-black w-4 h-4"
                              />
                              <span className="text-sm">
                                {label} <span className="text-red-500">*</span>
                                {hasError && !checked && (
                                  <span className="ml-2 text-xs text-red-600 font-medium">
                                    Required
                                  </span>
                                )}
                              </span>
                            </label>
                            {label === "Certificate Of Insurance Details" &&
                              insuranceLimitDocUrl && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowInsuranceLimits(true);
                                  }}
                                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 whitespace-nowrap"
                                  title="View required insurance limits"
                                >
                                  <DescriptionOutlinedIcon style={{ fontSize: 16 }} />
                                  Check Limits
                                </button>
                              )}
                          </div>
                          {checked && (
                            <div className="px-3 pb-3">
                              {isLinked && (
                                <div className="mb-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                                  Auto-filled from Q2 (Safety Manual) on the previous page. Uploading a new file here will override it for this row.
                                </div>
                              )}
                              <UploadBox
                                label={`Upload ${label}`}
                                file={file}
                                onChange={(f) => {
                                  clearError();
                                  setFormData((prev) => ({
                                    ...prev,
                                    enclosedDocumentFiles: {
                                      ...(prev.enclosedDocumentFiles || {}),
                                      [label]: f,
                                    },
                                  }));
                                }}
                                onRemove={() =>
                                  setFormData((prev) => {
                                    const next = { ...(prev.enclosedDocumentFiles || {}) };
                                    delete next[label];
                                    return { ...prev, enclosedDocumentFiles: next };
                                  })
                                }
                              />
                              {hasError && (
                                <p className="text-xs text-red-600 mt-2 text-left">
                                  Required — please check the box and upload this document.
                                </p>
                              )}
                              {label === "Certificate Of Insurance Details" && (
                                <div className="mt-3 flex flex-col gap-4">
                                  <div className="flex flex-col">
                                    <label className="text-sm font-medium mb-1 text-left">
                                      Insurance Expiry Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                      type="date"
                                      value={formData.insuranceExpiryDate || ""}
                                      onChange={(e) => {
                                        clearError();
                                        handleChange("insuranceExpiryDate", e.target.value);
                                      }}
                                      className="border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400 w-full md:w-60"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                      Required — surfaces on the admin dashboard as “Insurance Expires”.
                                    </p>
                                  </div>

                                  {coverageKeys.length > 0 && (
                                    <div className="rounded-lg border border-gray-200 bg-white p-3">
                                      <div className="flex items-baseline justify-between mb-2">
                                        <h4 className="text-sm font-semibold text-gray-900 text-left">
                                          Required Coverage Amounts
                                        </h4>
                                        <span className="text-[11px] text-gray-500">
                                          Submissions below the minimum will be auto-declined.
                                        </span>
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {coverageKeys.map((k) => {
                                          const min = Number(
                                            insuranceConfig.minimums[k] || 0,
                                          );
                                          const val = formData.coverages?.[k] || "";
                                          const numericVal = Number(val) || 0;
                                          const failing =
                                            val !== "" && numericVal < min;
                                          return (
                                            <div key={k} className="flex flex-col text-left">
                                              <label className="text-xs font-medium text-gray-700 mb-1">
                                                {insuranceConfig.labels[k] || k}
                                                <span className="ml-1 text-[10px] font-normal text-gray-500">
                                                  (min ${min.toLocaleString()})
                                                </span>
                                              </label>
                                              <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">$</span>
                                                <input
                                                  type="number"
                                                  min={0}
                                                  inputMode="numeric"
                                                  value={val}
                                                  onChange={(e) => {
                                                    const next = {
                                                      ...(formData.coverages || {}),
                                                      [k]: e.target.value,
                                                    };
                                                    handleChange("coverages", next);
                                                  }}
                                                  placeholder={String(min)}
                                                  className={`w-full pl-6 pr-3 py-2 text-sm border rounded-md bg-gray-50 focus:outline-none focus:border-gray-400 ${
                                                    failing
                                                      ? "border-red-300 bg-red-50"
                                                      : "border-gray-200"
                                                  }`}
                                                />
                                              </div>
                                              {failing && (
                                                <p className="text-[11px] text-red-600 mt-1">
                                                  Below required minimum — submission will be declined.
                                                </p>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1 && mode !== "new"}
            className="border border-gray-400 text-gray-600 px-8 py-2 rounded-md hover:bg-gray-100 disabled:text-gray-300 disabled:cursor-not-allowed flex items-center gap-1"
          >
            &lt; Previous
          </button>
          <span className="text-sm text-gray-400 self-center">{currentPage} / {TOTAL_STEPS}</span>
          {currentPage < TOTAL_STEPS ? (
            <button
              onClick={goToNextPage}
              className="bg-gray-900 text-white px-8 py-2 rounded-md hover:bg-gray-800 flex items-center gap-1"
            >
              Next &gt;
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="bg-gray-900 text-white px-8 py-2 rounded-md hover:bg-gray-800 flex items-center gap-1"
            >
              <CheckIcon fontSize="small" /> Submit
            </button>
          )}
        </div>
          </>
        )}
      </div>

      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        handleClose={() => setToastOpen(false)}
      />

      {/* OTP Verification Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] px-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Verify Phone Number</h3>
            <p className="text-sm text-gray-500 mb-5">
              Enter the 4-digit code sent to {formData.phone}
            </p>

            <div className="flex justify-center gap-3 mb-4">
              {otpDigits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (otpInputsRef.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={d}
                  onChange={(e) => handleOtpDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpDigitKeyDown(i, e)}
                  className="w-12 h-14 text-center text-lg border border-gray-300 rounded-md focus:outline-none focus:border-gray-800"
                />
              ))}
            </div>

            <div className="text-center text-xs text-gray-500 mb-4">
              Didn't receive a code?{" "}
              <button
                type="button"
                onClick={() => {
                  if (otpResendTimer > 0) return;
                  handleSendPhoneOtp();
                }}
                disabled={otpResendTimer > 0}
                className={`underline ${otpResendTimer > 0 ? "text-gray-400 cursor-not-allowed" : "text-gray-700"}`}
              >
                Resend Code
                {otpResendTimer > 0 ? ` (0:${otpResendTimer.toString().padStart(2, "0")})` : ""}
              </button>
            </div>

            {otpError && (
              <div className="text-red-600 text-sm mb-3 text-center">{otpError}</div>
            )}

            <button
              onClick={handleVerifyPhoneOtp}
              disabled={loader}
              className="w-full bg-gray-900 text-white text-sm font-medium py-3 rounded-md hover:bg-gray-800 transition disabled:opacity-60"
            >
              Verify
            </button>
            <button
              onClick={() => {
                setShowOtpModal(false);
                setOtpDigits(["", "", "", ""]);
                setOtpError("");
              }}
              className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showInsuranceLimits && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] px-4"
          onClick={() => setShowInsuranceLimits(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <div className="flex items-center gap-2">
                <DescriptionOutlinedIcon style={{ fontSize: 20, color: "#374151" }} />
                <h3 className="text-base font-semibold text-gray-900">
                  Required Insurance Limits
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={insuranceLimitDocUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                >
                  Open in new tab
                </a>
                <button
                  type="button"
                  onClick={() => setShowInsuranceLimits(false)}
                  className="text-gray-500 hover:text-gray-800 p-1"
                  aria-label="Close"
                >
                  <CloseIcon />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-gray-100">
              <iframe
                src={insuranceLimitDocUrl}
                title="NTACT Subcontractor Insurance Limits"
                className="w-full h-full border-0"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubcontractorForm;
